/**
 * moonshot-store-picker.js
 *
 * Wires up:
 *   - The header pill (snippets/moonshot-store-pill.liquid)
 *   - The popover (sections/moonshot-store-picker.liquid)
 *
 * Behavior (plan §5.1, §5.2):
 *   - Default silently to Noblesville on first visit. NO modal.
 *   - Pill shows current store, always visible, unobtrusive.
 *   - Click pill -> popover opens.
 *   - Click outside or Esc -> popover closes.
 *   - Choose a store -> setHomeStore, emit event, close.
 *   - "Use my location" -> opt-in geolocation, finds nearest group by
 *     distance to configured lat/lng, offers it as a suggestion.
 *   - ZIP entry -> simple bucketing (dev-store has no full ZIP database;
 *     we use prefix rules configurable via Moonshot.config.zipRules).
 */

(function () {
  "use strict";

  const M = window.Moonshot;
  if (!M) {
    console.warn("[Moonshot] moonshot-common.js not loaded; picker disabled.");
    return;
  }

  // ZIP-prefix rules. Simple and transparent.
  // Override via Moonshot.config.zipRules if you want a different split.
  M.config.zipRules = M.config.zipRules || [
    { prefix: "460", group: "noblesville" }, // Hamilton County area
    { prefix: "461", group: "plainfield" }, // SW of Indy
    { prefix: "462", group: "noblesville" }, // Default-ish for central Indy
  ];

  // --------------------------------------------------------------------------
  // DOM references, lazily resolved so the script is safe to load anywhere
  // --------------------------------------------------------------------------
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }
  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  // --------------------------------------------------------------------------
  // Pill
  // --------------------------------------------------------------------------
  function updatePillLabels() {
    const current = M.getHomeStore();
    qsa("[data-moonshot-pill-label]").forEach(function (el) {
      el.textContent = M.labelFor(current);
    });
    qsa("[data-moonshot-pill]").forEach(function (pill) {
      pill.setAttribute("data-moonshot-current", current);
    });
  }

  function onPillClick(e) {
    const pill = e.target.closest("[data-moonshot-pill]");
    if (!pill) return;
    e.preventDefault();
    openPicker();
  }

  // --------------------------------------------------------------------------
  // Popover
  // --------------------------------------------------------------------------
  let lastFocus = null;

  function openPicker() {
    const picker = qs("[data-moonshot-picker]");
    if (!picker) return;
    lastFocus = document.activeElement;
    picker.setAttribute("aria-hidden", "false");
    picker.classList.add("is-open");
    qsa("[data-moonshot-pill]").forEach(function (p) {
      p.setAttribute("aria-expanded", "true");
    });
    // Focus the title for screen readers, the first option for keyboards.
    const firstBtn = qs(".moonshot-picker__option-btn", picker);
    if (firstBtn) firstBtn.focus();
    document.addEventListener("keydown", onKey);
  }

  function closePicker() {
    const picker = qs("[data-moonshot-picker]");
    if (!picker) return;
    picker.setAttribute("aria-hidden", "true");
    picker.classList.remove("is-open");
    qsa("[data-moonshot-pill]").forEach(function (p) {
      p.setAttribute("aria-expanded", "false");
    });
    document.removeEventListener("keydown", onKey);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKey(e) {
    if (e.key === "Escape") closePicker();
  }

  function onPickerClick(e) {
    // Close on backdrop or any [data-moonshot-picker-close] click.
    if (e.target.closest("[data-moonshot-picker-close]")) {
      closePicker();
      return;
    }
    // Choose a store.
    const groupBtn = e.target.closest("[data-moonshot-group]");
    if (groupBtn) {
      const group = groupBtn.getAttribute("data-moonshot-group");
      M.setHomeStore(group, { syncAccount: true });
      setStatus("Now shopping " + M.labelFor(group) + ".");
      setTimeout(closePicker, 350);
      return;
    }
    // Geolocation.
    if (e.target.closest("[data-moonshot-geo]")) {
      handleGeo();
      return;
    }
  }

  function onZipSubmit(e) {
    if (!e.target.matches("[data-moonshot-zip-form]")) return;
    e.preventDefault();
    const input = qs("[data-moonshot-zip-input]", e.target);
    const zip = (input && input.value ? input.value : "").trim();
    if (!/^\d{5}$/.test(zip)) {
      setStatus("Please enter a 5-digit ZIP.");
      return;
    }
    const group = groupForZip(zip);
    if (!group) {
      setStatus("We don't recognize that ZIP yet — picking Noblesville as the default.");
      M.setHomeStore(M.config.defaultHomeStore, { syncAccount: true });
    } else {
      M.setHomeStore(group, { syncAccount: true });
      setStatus("Based on " + zip + ", we picked " + M.labelFor(group) + ".");
    }
    setTimeout(closePicker, 700);
  }

  function groupForZip(zip) {
    const rules = M.config.zipRules || [];
    for (let i = 0; i < rules.length; i++) {
      if (zip.indexOf(rules[i].prefix) === 0) return rules[i].group;
    }
    return null;
  }

  // --------------------------------------------------------------------------
  // Geolocation (opt-in button only, NEVER auto-prompt)
  // --------------------------------------------------------------------------
  // Haversine distance in km, just enough precision for store picker.
  function distanceKm(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  function handleGeo() {
    if (!navigator.geolocation) {
      setStatus("Your browser doesn't support location.");
      return;
    }
    setStatus("Finding your nearest store…");
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        const g = nearestGroup(pos.coords.latitude, pos.coords.longitude);
        if (!g) {
          setStatus("No nearby store — staying on " + M.labelFor(M.getHomeStore()) + ".");
          return;
        }
        M.setHomeStore(g, { syncAccount: true });
        setStatus("Closest is " + M.labelFor(g) + ".");
        setTimeout(closePicker, 700);
      },
      function () {
        setStatus("Location not available. You can pick a store above.");
      },
      { timeout: 8000, maximumAge: 60000 },
    );
  }

  // The geo coordinates live on location metafields (geo_lat, geo_lng). The
  // theme Liquid renders them to a window.Moonshot.config.geo map at load time
  // so we don't need an extra API round-trip. See integration-guide.md.
  function nearestGroup(lat, lng) {
    const geo = M.config.geo || {};
    let best = null;
    let bestDist = Infinity;
    Object.keys(geo).forEach(function (group) {
      const coords = geo[group];
      if (!coords || typeof coords.lat !== "number" || typeof coords.lng !== "number") return;
      const d = distanceKm(lat, lng, coords.lat, coords.lng);
      if (d < bestDist) {
        bestDist = d;
        best = group;
      }
    });
    return best;
  }

  // --------------------------------------------------------------------------
  // Status line inside the popover
  // --------------------------------------------------------------------------
  function setStatus(msg) {
    const el = qs("[data-moonshot-picker-status]");
    if (el) el.textContent = msg || "";
  }

  // --------------------------------------------------------------------------
  // Wire up
  // --------------------------------------------------------------------------
  function init() {
    updatePillLabels();
    document.addEventListener("click", onPillClick);
    const picker = qs("[data-moonshot-picker]");
    if (picker) picker.addEventListener("click", onPickerClick);
    document.addEventListener("submit", onZipSubmit);

    // Any element with data-moonshot-open-picker opens the popover.
    // Useful for a PDP "Switch store?" inline link.
    document.addEventListener("click", function (e) {
      if (e.target.closest("[data-moonshot-open-picker]")) {
        e.preventDefault();
        openPicker();
      }
    });

    // Keep pill labels synced across tabs / programmatic changes.
    M.on("home-store:changed", updatePillLabels);
    window.addEventListener("storage", function (ev) {
      if (ev.key === M.config.cookieName) updatePillLabels();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
